import NavigationArrow from "@/assets/NavigationArrow";
import { cn } from "@/lib/utils";
import { EmblaOptionsType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import Image from "next/image";
import React, { ReactNode, useCallback, useEffect, useState } from "react";

type Props = {
  children: ReactNode[];
  options?: EmblaOptionsType;
  left_navigation_icon?: string;
  right_navigation_icon?: string;
  startIndex?: number;
  onNavigationClick?: (
    direction: "prev" | "next",
    currentIndex: number
  ) => void;
};

export default function EmblaPeekCarousel({
  children,
  options = {},
  left_navigation_icon,
  right_navigation_icon,
  startIndex = 0,
  onNavigationClick,
}: Props) {
  const defaultOptions: EmblaOptionsType = {
    align: "center",
    containScroll: "trimSnaps",
    loop: false,
    startIndex,
    ...options,
  };

  const [viewportRef, embla] = useEmblaCarousel(defaultOptions);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  const scrollPrev = useCallback(() => {
    if (embla) {
      embla.scrollPrev();
      const newIndex = embla.selectedScrollSnap();
      setSelectedIndex(newIndex);
      onNavigationClick?.("prev", newIndex);
    }
  }, [embla, onNavigationClick]);

  const scrollNext = useCallback(() => {
    if (embla) {
      embla.scrollNext();
      const newIndex = embla.selectedScrollSnap();
      setSelectedIndex(newIndex);
      onNavigationClick?.("next", newIndex);
    }
  }, [embla, onNavigationClick]);

  useEffect(() => {
    if (!embla) return undefined;
    const onSelect = () => {
      setSelectedIndex(embla.selectedScrollSnap());
      setCanScrollPrev(embla.canScrollPrev());
      setCanScrollNext(embla.canScrollNext());
    };
    embla.on("select", onSelect);
    onSelect();
    return () => {
      embla.off("select", onSelect);
    };
  }, [embla]);

  return (
    <div className="relative w-full flex flex-col gap-6 max-w-4xl mx-auto">
      <div
        ref={viewportRef}
        className="overflow-hidden"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className="flex xs:-mx-1 sm:-mx-2">
          {React.Children.map(children, (child, index) => (
            <div
              key={index}
              className="xs:flex-[0_0_196px] sm:flex-[0_0_50%]  xs:px-1 sm:px-2 box-border"
            >
              <div className="h-full w-full rounded-xl shadow-md overflow-hidden">
                {child}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center w-full">
        <div className="flex items-center gap-5 mt-4">
          <div className="cursor-pointer" onClick={scrollPrev}>
            {left_navigation_icon && left_navigation_icon !== "" ? (
              <div
                className={cn(
                  "w-[36px] h-[36px]",
                  "sm:w-[38px] sm:h-[38px]",
                  "md:w-[42px] md:h-[42px]",
                  "lg:w-[56px] lg:h-[56px]",
                  canScrollPrev ? "opacity-100" : "opacity-30"
                )}
              >
                <Image
                  src={left_navigation_icon}
                  alt="Left navigation"
                  width={100}
                  height={100}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <NavigationArrow
                fill={selectedIndex === 0 ? "#F8B6BC" : "#f74958"}
                pathFill={"white"}
                className={cn(
                  "rotate-180",
                  "xs:size-9",
                  "sm:size-[38px]",
                  "lg:size-[52px]"
                )}
              />
            )}
          </div>
          <div className="cursor-pointer" onClick={scrollNext}>
            {right_navigation_icon && right_navigation_icon !== "" ? (
              <div
                className={cn(
                  "w-[36px] h-[36px]",
                  "sm:w-[38px] sm:h-[38px]",
                  "md:w-[42px] md:h-[42px]",
                  "lg:w-[56px] lg:h-[56px]",
                  canScrollNext ? "opacity-100" : "opacity-30"
                )}
              >
                <Image
                  src={right_navigation_icon}
                  alt="Right navigation"
                  width={100}
                  height={100}
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <NavigationArrow
                fill={
                  selectedIndex === React.Children.toArray(children).length - 1
                    ? "#F8B6BC"
                    : "#f74958"
                }
                pathFill={"white"}
                className={cn("xs:size-9", "sm:size-[38px]", "lg:size-[52px]")}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
